import React from 'react';
import { Button, Table, TableContainer } from '@odin-labs/components';
import { useGetJobsiteDataSharingGrantsQuery } from 'apollo/generated/client-operations';
import { NewHeader } from 'components/header/NewHeader';
import { PlusIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { useModalChangeActions } from 'utils/useModalChangeActions';
import { Editable } from 'types';
import { getColumns } from './tables';
import { DataSharingGrant } from './types';
import {
  AddDataSharingGrantModal,
  RevokeDataSharingGrantModal,
} from './modals';

export function DataSharingTab(
  props: JobsiteConfigTabProps,
): React.ReactElement {
  const { jobsiteId, onTabApiChange } = props;

  const { data, loading, error, refetch } = useGetJobsiteDataSharingGrantsQuery(
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: { jobsiteId },
    },
  );

  const jobsiteName = data?.getJobsite?.name;
  const grants = data?.jobsiteDataSharingGrants;
  const grantsCount = grants?.length;

  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        loading,
        jobsiteName,
      }),
    [onTabApiChange, refetch, loading, jobsiteName],
  );

  const { change, closeModal, openAddModal, openDeleteModal } =
    useModalChangeActions<Editable<DataSharingGrant>>();

  const columns = React.useMemo(
    () => getColumns({ openRevokeModal: openDeleteModal }),
    [openDeleteModal],
  );

  const closeModalAndRefresh = (): void => {
    closeModal();
    refetch();
  };

  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <>
      <TableContainer>
        <NewHeader
          size="md"
          title="Data sharing"
          titleInfo={grantsCount}
          actionsProps={{
            headerActions: null,
            children: (
              <Button
                icon={PlusIcon}
                text="Add grant"
                hideTextOnMobile
                size={['base', 'md:sm']}
                onClick={openAddModal}
              />
            ),
          }}
        />
        <Table
          loading={loading}
          columns={columns}
          data={grants}
          cellClassName="!odin-pl-5"
          remote
          disableGlobalFilter
        />
      </TableContainer>
      <AddDataSharingGrantModal
        isOpen={change?.type === 'create'}
        jobsiteId={jobsiteId}
        onCancel={closeModal}
        onConfirm={closeModalAndRefresh}
      />
      <RevokeDataSharingGrantModal
        isOpen={change?.type === 'remove'}
        grant={change?.item}
        onCancel={closeModal}
        onConfirm={closeModalAndRefresh}
      />
    </>
  );
}
